import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from '@mui/material';
import axios from 'axios';
import './AdPage.css';
import HighlightText from './HighlightText';

const AdPage = ({ user }) => {
    const { adId } = useParams();
    const navigate = useNavigate();
    const [adText, setAdText] = useState('');
    const [typedText, setTypedText] = useState('');
    const [startTime, setStartTime] = useState(null);
    const [wpm, setWpm] = useState(0);
    const [accuracy, setAccuracy] = useState(100);
    const [openConfirm, setOpenConfirm] = useState(false);

    useEffect(() => {
        const fetchAdText = async () => {
            try {
                const response = await axios.get(`https://api.typecopy.co.za/api/Ads/${adId}`);
                setAdText(response.data.adCopy);
            } catch (error) {
                console.error('Error fetching ad text:', error);
            }
        };

        fetchAdText();
    }, [adId]);

    const calculateStats = useCallback(() => {
        const endTime = new Date();
        const timeDiff = (endTime - startTime) / 1000 / 60;
        const wordsTyped = typedText.split(' ').length;
        const calculatedWpm = wordsTyped / timeDiff;
        setWpm(calculatedWpm);

        const correctChars = typedText.split('').filter((char, index) => char === adText[index]).length;
        const calculatedAccuracy = (correctChars / typedText.length) * 100;
        setAccuracy(calculatedAccuracy);
    }, [typedText, adText, startTime]);

    useEffect(() => {
        if (typedText.length === 1) {
            setStartTime(new Date());
        }

        if (typedText.length > 0) {
            calculateStats();
        }
    }, [typedText, calculateStats]);

    useEffect(() => {
        const handleKeyPress = (event) => {
            const { key } = event;
            if (key.length === 1) {
                setTypedText((prev) => prev + key);
            } else if (key === 'Backspace') {
                setTypedText((prev) => prev.slice(0, -1));
            } else if (key === 'Enter') {
                setTypedText((prev) => prev + '\n');
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, []);

    const handleCompletion = () => {
        setOpenConfirm(true);
    };

    const handleConfirm = async () => {
        setOpenConfirm(false);
        try {
            await updateStats(wpm, accuracy);
            navigate('/stats');
        } catch (error) {
            console.error('Error updating stats:', error);
        }
    };

    const handleCancel = () => {
        setOpenConfirm(false);
    };

    const updateStats = async (wpm, accuracy) => {
        if (!user || !user.id) {
            console.error('User information is missing');
            return;
        }

        try {
            await axios.post('https://api.typecopy.co.za/api/UserStats', {
                id: 0,
                userId: user.id, 
                username: user.username, 
                adsCompleted: 1,
                averageTimeSpent: (new Date() - startTime) / 1000 / 60, 
                wordsPerMinute: wpm,
                accuracy: accuracy 
            });
        } catch (error) {
            console.error('Error updating stats:', error);
        }
    };

    return (
        <Container>
            <Typography variant="h5" gutterBottom>
                Ad Typing Session
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>
                <HighlightText adText={adText} typedText={typedText} />
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Words per minute: {wpm.toFixed(2)}
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Accuracy: {accuracy.toFixed(2)}%
            </Typography>
            <Button variant="contained" color="primary" onClick={handleCompletion}>
                Done
            </Button>
            <Dialog open={openConfirm} onClose={handleCancel}>
                <DialogTitle>Confirm Completion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to complete this Ad?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} color="primary">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdPage;